import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiGrid, FiBriefcase, FiUsers, FiLayers, FiZap, FiSettings } from 'react-icons/fi';
import { clsx } from 'clsx';
import { useApp } from '@/contexts/AppContext';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: FiGrid },
  { label: 'Companies', path: '/companies', icon: FiBriefcase },
  { label: 'Usuarios', path: '/users', icon: FiUsers },
  { label: 'Planos', path: '/plans', icon: FiLayers },
  { label: 'Integracoes', path: '/integrations', icon: FiZap },
  { label: 'Configuracoes', path: '/settings', icon: FiSettings },
];

interface SidebarProps {
  collapsed: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const location = useLocation();
  const { app } = useApp();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={clsx('flex flex-col h-screen border-r transition-all duration-300', collapsed ? 'w-[68px]' : 'w-[240px]')}
      style={{
        backgroundColor: 'var(--theme-sidebar-background)',
        borderColor: 'var(--theme-sidebar-border)',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center h-[56px] px-4 border-b"
        style={{ borderColor: 'var(--theme-sidebar-border)' }}
      >
        {app?.logo ? (
          <img
            src={app.logo}
            alt={app.name || 'Logo'}
            className="h-8 w-8 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div
            className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 text-sm font-bold"
            style={{ backgroundColor: 'var(--theme-primary)', color: 'var(--theme-button-primary-text)' }}
          >
            {app?.name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
        )}
        {!collapsed && (
          <span className="ml-3 font-semibold text-sm truncate" style={{ color: 'var(--theme-header-text)' }}>
            {app?.name || 'Admin Panel'}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
                collapsed && 'justify-center px-2'
              )}
              style={{
                backgroundColor: active ? 'var(--theme-sidebar-active)' : 'transparent',
                color: active ? 'var(--theme-sidebar-active-text)' : 'var(--theme-sidebar-text)',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.color = 'var(--theme-sidebar-text-hover)';
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.color = 'var(--theme-sidebar-text)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <item.icon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
};
